import { of, switchMap, tap, merge, fromEvent, Subject, mergeMap, first } from 'rxjs';

import { replaceViewContainer, renderTemplate, getParentAttr } from './ui.js';
import { collections$, addCollection, updateCollection, updateCollectionMembership } from './knowledge-api.js';
import { buildWorkMonitor } from './working.js';
import { userInfo$ } from './http.js';

export function createCollectionsView() {
    return of(undefined).pipe(
        switchMap(() => {
            let root = renderTemplate('collections-view');
            replaceViewContainer(root);

            let saveMonitor = buildWorkMonitor();
            let saveIndicator$ = saveMonitor.showElements('.saving');
            let alertSavingBeforeUnload$ = saveMonitor.alertBeforeUnload();

            let collectionsContainer = root.querySelector('.collections-container');

            let renderCollections$ = collections$.pipe(
                tap(collections => {
                    collectionsContainer.innerHTML = '';
                    if (collections.length === 0) {
                        collectionsContainer.textContent = 'You don\'t have any collections yet.';
                    } else {
                        for (let col of collections) {
                            let colEl = renderTemplate('collection-card-widget');
                            colEl.setAttribute('data-col-id', `${col.id}`);
                            colEl.setAttribute('data-is-owner', col.is_owner ? '1' : '0');
                            let ownerUseEl = colEl.querySelector('.use');
                            ownerUseEl.checked = col.owner_use;
                            ownerUseEl.addEventListener('change', handleToggleOwnerUse, false);
                            let colLink = colEl.querySelector('.collection-link');
                            colLink.textContent = `${col.owner}/${col.name}`;
                            colLink.href = `#collection/${col.id}`;
                            collectionsContainer.appendChild(colEl);
                        }
                    }
                }),
            );

            let addCollectionForm = root.querySelector('form[name=addCollectionForm]');
            let securityLevelFieldEl = addCollectionForm.querySelector('.security-level-field');

            let applyUserInfo$ = userInfo$.pipe(
                first(),
                tap(userInfo => {
                    if (!userInfo.security_level_enabled) {
                        securityLevelFieldEl.classList.add('hidden');
                    }
                }),
            );

            let addCollectionSubmit$ = fromEvent(addCollectionForm, 'submit').pipe(
                switchMap(event => {
                    event.preventDefault();
                    let collection = {
                        name: addCollectionForm.name.value,
                    };
                    if (!securityLevelFieldEl.classList.contains('hidden')) {
                        collection.security_level = parseInt(addCollectionForm.securityLevel.value);
                    }
                    return saveMonitor.zone(addCollection(collection));
                }),
                tap(() => addCollectionForm.reset()),
            );

            let updateCollectionProperties$ = new Subject();
            let workCollectionProperties$ = updateCollectionProperties$.pipe(
                mergeMap(({ colId, col }) => {
                    let work$ = 'member_use' in col
                        ? updateCollectionMembership(colId, col.member_use)
                        : updateCollection(colId, col);
                    return saveMonitor.zone(work$);
                }, undefined, 1),
            );

            return merge(
                saveIndicator$,
                alertSavingBeforeUnload$,
                renderCollections$,
                applyUserInfo$,
                addCollectionSubmit$,
                workCollectionProperties$,
            );

            function handleToggleOwnerUse(event) {
                let colId = parseInt(getParentAttr(event.target, 'data-col-id'));
                let isOwner = getParentAttr(event.target, 'data-is-owner') !== '0';
                if (isOwner) {
                    updateCollectionProperties$.next({
                        colId,
                        col: {
                            owner_use: event.target.checked,
                        },
                    });
                } else {
                    updateCollectionProperties$.next({
                        colId,
                        col: {
                            member_use: event.target.checked,
                        },
                    });
                }
            }
        }),
    );
}
